<template>
    <div class="case-problems-field">
        <div class="problems-table">
            <Draggable v-model="localProblems"
                      handle=".drag-handle"
                      @end="onDragEnd"
                      tag="div"
                      class="table-body">
                <div v-for="(problem, index) in localProblems" :key="index"
                     class="table-row">
                    <!-- Drag Handle -->
                    <div class="table-cell drag-handle-column">
                        <div class="drag-handle">
                            <i class="fas fa-grip-vertical"></i>
                        </div>
                    </div>

                    <!-- Problem Name -->
                    <div class="table-cell problem-name-cell">
                        <b-form-input
                            v-model="problem.name"
                            placeholder="Problem Name"
                            :maxlength="50"
                            @input="emitChange"
                        />
                    </div>

                    <!-- Next Touch Days -->
                    <div class="table-cell next-touch-cell">
                        <b-form-input
                            v-model.number="problem.nextTouchDays"
                            type="number"
                            placeholder="Next Touch Days"
                            min="0"
                            @input="emitChange"
                        />
                    </div>

                    <!-- Watchers -->
                    <div class="table-cell watchers-cell">
                        <multiselect
                            v-model="problem.watchersOptions"
                            :options="watchersOptions"
                            :multiple="true"
                            group-values="options"
                            group-label="type"
                            label="name"
                            track-by="id"
                            placeholder="Select default watchers (optional)"
                            @input="onWatchersChange(index, $event)"
                        >
                            <template #option="data">
                                <i v-if="data.option.isTeam" class="fa fa-users"></i>
                                <i v-else class="fa fa-user"></i>
                                {{ data.option.$groupLabel }}
                                {{ data.option.name }}
                            </template>

                            <template #tag="data">
                                <b-badge v-if="data.option.isTeam" variant="primary" class="mr-1 my-0">
                                    <i class="fa fa-users"></i>
                                    {{ data.option.name }}
                                    <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                </b-badge>
                                <b-badge v-else variant="info" class="mr-1 my-0">
                                    <i class="fa fa-user"></i>
                                    {{ data.option.name }}
                                    <i class="fa fa-close ml-1" @click="data.remove(data.option)"></i>
                                </b-badge>
                            </template>
                        </multiselect>
                    </div>

                    <!-- Assignment -->
                    <div class="table-cell assignment-cell">
                        <multiselect
                            v-model="problem.assignmentOption"
                            :options="watchersOptions"
                            :multiple="false"
                            group-values="options"
                            group-label="type"
                            label="name"
                            track-by="id"
                            placeholder="Select default assignment (optional)"
                            @input="onAssignmentChange(index, $event)"
                        >
                            <template #option="data">
                                <i v-if="data.option.isTeam" class="fa fa-users"></i>
                                <i v-else class="fa fa-user"></i>
                                {{ data.option.$groupLabel }}
                                {{ data.option.name }}
                            </template>

                            <template #singleLabel="data">
                                <b-badge v-if="data.option.isTeam" variant="primary">
                                    <i class="fa fa-users"></i>
                                    {{ data.option.name }}
                                </b-badge>
                                <b-badge v-else variant="info">
                                    <i class="fa fa-user"></i>
                                    {{ data.option.name }}
                                </b-badge>
                            </template>
                        </multiselect>
                    </div>

                    <!-- Actions -->
                    <div class="table-cell actions-cell">
                        <b-button variant="link" @click="removeProblem(index)" title="Remove">
                            <b-icon icon="x-circle-fill" variant="danger" />
                        </b-button>
                    </div>
                </div>
            </Draggable>
        </div>

        <b-button variant="primary" size="sm" @click="addProblem" class="mt-2">
            <i class="fa fa-plus" /> Add Problem
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop, Watch } from 'vue-property-decorator';
import Multiselect from 'vue-multiselect';
import Draggable from 'vuedraggable';
import { Team } from '@/models/Team';
import { User } from '@/models/User';
import { NotificationOptions } from '@/util/NotificationOptionsPresets';

interface WatcherOption {
    id: string | number;
    name: string;
    isTeam?: boolean;
}

interface WatcherData {
    teamId?: number;
    userName?: string;
    teamName?: string;
}

interface AssignmentData {
    teamId?: number;
    userName?: string;
    teamName?: string;
}

interface CaseProblemConfig {
    name: string;
    nextTouchDays?: number | null;
    watchers: WatcherData[];
    watchersOptions?: WatcherOption[]; // Internal UI state
    assignment?: AssignmentData | null;
    assignmentOption?: WatcherOption | null; // Internal UI state
}

@Component({
    name: "CaseProblemsField",
    components: {
        Multiselect,
        Draggable
    }
})
export default class CaseProblemsField extends Vue {
    @Prop({ default: () => [] }) value!: any;

    protected teams: Team[] = [];
    protected users: User[] = [];
    protected localProblems: CaseProblemConfig[] = [];

    async mounted() {
        await this.loadTeamsAndUsers();
        this.initializeProblems();
    }

    async loadTeamsAndUsers() {
        try {
            const [teamsResponse, usersResponse] = await Promise.all([
                this.$http.get<Team[]>("/user/teams"),
                this.$http.get<User[]>("/user")
            ]);
            this.teams = teamsResponse.data;
            this.users = usersResponse.data;
        } catch (err) {
            this.$notification(NotificationOptions.error(err));
            console.error(err);
        }
    }

    initializeProblems() {
        if (!this.value || this.value.length === 0) {
            this.localProblems = [];
            return;
        }

        // Handle both string array (legacy) and object array (new format)
        if (typeof this.value[0] === 'string') {
            // Convert legacy string array to new format
            this.localProblems = this.value.map((name: string) => ({
                name,
                nextTouchDays: null,
                watchers: [],
                watchersOptions: [],
                assignment: null,
                assignmentOption: null
            }));
        } else {
            // Already in new format
            this.localProblems = this.value.map((problem: any) => ({
                name: problem.name || '',
                nextTouchDays: problem.nextTouchDays || null,
                watchers: problem.watchers || [],
                watchersOptions: this.watchersDataToOptions(problem.watchers || []),
                assignment: problem.assignment || null,
                assignmentOption: problem.assignment ? this.assignmentDataToOption(problem.assignment) : null
            }));
        }
    }

    watchersDataToOptions(watchersData: WatcherData[]): WatcherOption[] {
        return watchersData.map(w => ({
            id: w.teamId ? w.teamId : w.userName!,
            name: w.teamId ? w.teamName! : w.userName!,
            isTeam: !!w.teamId
        }));
    }

    watchersOptionsToData(watchersOptions: WatcherOption[]): WatcherData[] {
        return watchersOptions.map(w => ({
            teamId: w.isTeam ? w.id as number : undefined,
            userName: !w.isTeam ? w.id as string : undefined,
            teamName: w.isTeam ? w.name : undefined
        }));
    }

    assignmentDataToOption(assignmentData: AssignmentData): WatcherOption | null {
        if (!assignmentData || (!assignmentData.teamId && !assignmentData.userName)) {
            return null;
        }
        return {
            id: assignmentData.teamId ? assignmentData.teamId : assignmentData.userName!,
            name: assignmentData.teamId ? assignmentData.teamName! : assignmentData.userName!,
            isTeam: !!assignmentData.teamId
        };
    }

    assignmentOptionToData(assignmentOption: WatcherOption | null): AssignmentData | null {
        if (!assignmentOption) {
            return null;
        }
        return {
            teamId: assignmentOption.isTeam ? assignmentOption.id as number : undefined,
            userName: !assignmentOption.isTeam ? assignmentOption.id as string : undefined,
            teamName: assignmentOption.isTeam ? assignmentOption.name : undefined
        };
    }

    get watchersOptions() {
        return [
            {
                type: 'Teams',
                options: this.teams.map(t => ({
                    id: t.id,
                    name: t.name,
                    isTeam: true
                }))
            },
            {
                type: 'Users',
                options: this.users.map(u => ({
                    id: u.userName,
                    name: u.userName,
                    isTeam: false
                }))
            }
        ];
    }

    addProblem() {
        this.localProblems.push({
            name: '',
            nextTouchDays: null,
            watchers: [],
            watchersOptions: [],
            assignment: null,
            assignmentOption: null
        });
    }

    removeProblem(index: number) {
        this.localProblems.splice(index, 1);
        this.emitChange();
    }

    onWatchersChange(index: number, watchersOptions: WatcherOption[]) {
        this.localProblems[index].watchersOptions = watchersOptions;
        this.localProblems[index].watchers = this.watchersOptionsToData(watchersOptions);
        this.emitChange();
    }

    onAssignmentChange(index: number, assignmentOption: WatcherOption | null) {
        this.localProblems[index].assignmentOption = assignmentOption;
        this.localProblems[index].assignment = this.assignmentOptionToData(assignmentOption);
        this.emitChange();
    }

    onDragEnd() {
        this.emitChange();
    }

    emitChange() {
        // Emit the data in the format expected by the backend
        const output = this.localProblems
            .filter(p => p.name.trim() !== '') // Remove empty problems
            .map(p => ({
                name: p.name.trim(),
                nextTouchDays: p.nextTouchDays,
                watchers: p.watchers,
                assignment: p.assignment
            }));
        this.$emit('input', output);
    }

    @Watch('value', { deep: true })
    onValueChanged() {
        // Avoid re-initializing while user is editing
        if (this.localProblems.length === 0 && this.value && this.value.length > 0) {
            this.initializeProblems();
        }
    }
}
</script>

<style scoped lang="scss">
.case-problems-field {
    width: 100%;
}

.problems-table {
    background-color: white;
    border: 1px solid #dee2e6;
    border-radius: 4px;
}

.table-body {
    display: block;
}

.table-row {
    display: flex;
    align-items: center;
    border-top: 1px solid #dee2e6;
    padding: 8px;
    background-color: white;
    min-height: 50px;

    &:first-child {
        border-top: none;
    }

    &:hover {
        background-color: rgba(0, 0, 0, 0.02);
    }
}

.table-cell {
    display: flex;
    align-items: center;
    padding: 0 6px;

    &.drag-handle-column {
        width: 40px;
        flex-shrink: 0;
        justify-content: center;
        cursor: move;
        color: #6c757d;

        .drag-handle {
            cursor: move;
            display: flex;
            align-items: center;
            justify-content: center;

            &:hover {
                color: #495057;
            }
        }
    }

    &.problem-name-cell {
        min-width: 200px;
        flex: 1;
    }

    &.next-touch-cell {
        min-width: 150px;
        flex-shrink: 0;
    }

    &.watchers-cell {
        flex: 2;
        min-width: 300px;
    }

    &.assignment-cell {
        flex: 1.5;
        min-width: 250px;
    }

    &.actions-cell {
        width: 50px;
        flex-shrink: 0;
        justify-content: center;
    }
}

@media (max-width: 900px) {
    .table-row {
        display: grid;
        grid-template-columns: 1fr auto;
        grid-template-rows: auto auto auto auto auto;
        gap: 8px;
        padding: 12px;
    }

    .table-cell {
        width: auto !important;
        min-width: 0 !important;
        flex: none !important;
        padding: 0;

        &.drag-handle-column {
            grid-column: 1 / 2;
            grid-row: 1;
            justify-content: flex-start;
        }

        &.actions-cell {
            grid-column: 2 / 3;
            grid-row: 1;
            justify-content: flex-end;
        }

        &.problem-name-cell {
            grid-column: 1 / 3;
            grid-row: 2;
        }

        &.next-touch-cell {
            grid-column: 1 / 3;
            grid-row: 3;
        }

        &.watchers-cell {
            grid-column: 1 / 3;
            grid-row: 4;
        }

        &.assignment-cell {
            grid-column: 1 / 3;
            grid-row: 5;
        }
    }
}
</style>
